import { useState } from 'react';
import { productLabel } from '../utils/productLabel';
import { BehaviorSubject } from 'rxjs';
import { HiddenAssetSource, HiddenRecord, InventoryAsset, EntityVoiceDetailsRecord, CustomFieldValue } from '../types';
import { statusFormatter, termFormatter } from '../utils/helpers';
import { addError } from './Error';
import { getSelectedAccountIdsForUrl } from './User';
import { fetchAggregateInventory, fetchAggregateInventoryById } from '../Api/Inventory/aggregateInventoryApi';
import { deleteOutsideInventory } from '../Api/OutsideInventory/outsideInventoryApi';
import { createHiddenAsset, deleteHiddenAsset, fetchHiddenAssets } from '../Api/Inventory/hiddenAssetApi';
import { useSnackbar } from '../Context/SnackbarContext';
import { getAccountIdBySfId } from './Accounts';
import { fetchProjects } from './Project';

const HIDE_NOUNS: Record<HiddenAssetSource, string> = {
  Inventory: 'Inventory',
  Benchmark: 'Benchmark record',
  Project: 'Project',
  Quote: 'Quote'
};
const UNHIDE_NOUNS: Record<HiddenAssetSource, string> = { ...HIDE_NOUNS, Inventory: 'Inventory asset' };

export interface UseInventoryReturn {
  fetchInventory: (forceRefresh?: boolean) => Promise<void>;
  fetchInventoryById: (inventoryId: string) => Promise<void>;
  setInventoryData: (inventory: InventoryAsset[]) => void;
  deleteInventoryById: (id: string) => Promise<void>;
  clear: () => void;
  inventory: InventoryAsset[];
  // Resolve false when the call failed, so callers can hold their ground instead of navigating away.
  createHiddenAsset: (assetId: string, accountId: string, source?: HiddenAssetSource) => Promise<boolean>;
  deleteHiddenAsset: (assetId: string, accountId: string, source?: HiddenAssetSource) => Promise<boolean>;
  fetchHiddenAssets: (accountId: string, forceRefresh?: boolean) => Promise<void>;
  hiddenAssets: HiddenRecord[];
}

export const isInventoryItem = (data: unknown): data is InventoryAsset => {
  return typeof data === 'object' && data !== null && !!(data as InventoryAsset)?.sfId;
};

const defaultInventoryData: InventoryAsset[] = [];

const subject = new BehaviorSubject<InventoryAsset[]>(defaultInventoryData);
const entityVoiceDetailsSubject = new BehaviorSubject<EntityVoiceDetailsRecord[]>([]);
const inventoryByIdSubject = new BehaviorSubject<InventoryAsset | undefined>(undefined);
const loadingSubject = new BehaviorSubject<boolean>(false);
const hiddenAssetsSubject = new BehaviorSubject<HiddenRecord[]>([]);
const emitErrorState = (message?: string) => addError('inventory', message);

export const getInventorySubject = (): BehaviorSubject<InventoryAsset[]> => subject;
export const getEntityVoiceDetailsSubject = (): BehaviorSubject<EntityVoiceDetailsRecord[]> =>
  entityVoiceDetailsSubject;
export const getInventoryByIdSubject = (): BehaviorSubject<InventoryAsset | undefined> => inventoryByIdSubject;
export const getInventoryLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;
export const getHiddenAssetsSubject = (): BehaviorSubject<HiddenRecord[]> => hiddenAssetsSubject;
export const getInventory = (): InventoryAsset[] => subject.value;
export const getInventoryById = (id: string): InventoryAsset | undefined =>
  subject.value.find((inv: InventoryAsset) => inv.id === id);
export const getAddress = (id: string) => {
  const inventory = subject.getValue();
  return inventory.find((i) => i.id === id)?.address;
};
export const getInventoryByProjectId = (projectId: string): InventoryAsset[] => {
  const inventory = subject.getValue();
  return inventory.filter((i) => i.projectId === projectId);
};

// Patch just the custom_fields of an inventory asset in the store so a grid re-reading it on remount
// reflects an inline edit without waiting for a full refetch.
export const setInventoryCustomFields = (assetId: string, customFields: CustomFieldValue[]): void =>
  subject.next(subject.value.map((it) => (it.id === assetId ? { ...it, custom_fields: customFields } : it)));

// Generic so a hidden record keeps its own fields (orderRequest, a null mRC) through normalization.
export const normalizeInventoryAsset = <T extends Omit<InventoryAsset, 'mRC'>>(i: T): T => {
  const isUpstackManaged = !i.outside_inventory_schema_id;
  return {
    ...i,
    productDisplay: i.productDisplay || productLabel(i.product),
    status: statusFormatter(i.status),
    term: isUpstackManaged ? termFormatter(i.term) : i.term,
    origin: isUpstackManaged
  };
};

const setEntityVoiceDetailsData = (data: InventoryAsset[]) => {
  const voiceDetails: EntityVoiceDetailsRecord[] = data.reduce(
    (acc: EntityVoiceDetailsRecord[], item: InventoryAsset) => {
      if (item.phoneNumbers && item.phoneNumbers.length > 0) {
        item.phoneNumbers.forEach((phone) => {
          acc.push({
            ...phone,
            entityId: item.id,
            entityType: 'inventory',
            entityName: item.address
          });
        });
      }
      return acc;
    },
    []
  );
  entityVoiceDetailsSubject.next(voiceDetails);
};

// Module-level so the shared grid column defs, which have no hook in scope, can refresh the list.
export const fetchInventory = async (forceRefresh = false): Promise<void> => {
  if (loadingSubject.getValue()) return;
  if (subject.getValue().length > 0 && !forceRefresh) return;
  // No account scope yet: an empty fetch errors, and the error branch below would wipe the store.
  if (getSelectedAccountIdsForUrl().length === 0) return;
  loadingSubject.next(true);
  try {
    const { data, error } = await fetchAggregateInventory(getSelectedAccountIdsForUrl());
    // Corporate proxies can replace a 200 body with non-JSON — treat anything non-array as an error.
    if (Array.isArray(data)) {
      emitErrorState();
      const inventoryAssets = data.map(normalizeInventoryAsset);
      subject.next(inventoryAssets);
      setEntityVoiceDetailsData(inventoryAssets);
    } else {
      subject.next(defaultInventoryData);
      emitErrorState(error?.message || 'Unexpected inventory response');
    }
  } catch (e) {
    // A throw here would otherwise wedge the loading flag on, and every reader of the store waits on it.
    emitErrorState(e instanceof Error ? e.message : String(e));
    subject.next(defaultInventoryData);
  } finally {
    loadingSubject.next(false);
  }
};

// Row-level refresh: a list fetch flips the loading flag, and the grid renders a spinner in its place.
export const fetchInventoryAssetById = async (id: string): Promise<'gone' | void> => {
  const { data, error } = await fetchAggregateInventoryById(id, getSelectedAccountIdsForUrl());
  const asset = Array.isArray(data) ? data[0] : (data as InventoryAsset | null);
  if (!asset) {
    // Not-found means the record left this caller's reach; any other failure is no evidence of that.
    if (error?.errorResponse?.response?.status === 404) {
      const remaining = subject.getValue().filter((item) => item.id !== id);
      subject.next(remaining);
      // Phone Numbers reads only this derived list, so it would keep the dropped row's numbers.
      setEntityVoiceDetailsData(remaining);
      // Cleared, not set: the caller announces this outcome, and nothing else ever empties this domain.
      emitErrorState();
      return 'gone';
    }
    emitErrorState(error?.message || 'Inventory item not found');
    return;
  }

  const refreshed = normalizeInventoryAsset(asset);
  // Merged rather than replaced: the single-asset read omits custom fields for internal users.
  subject.next(subject.getValue().map((item) => (item.id === id ? { ...item, ...refreshed } : item)));
  emitErrorState();
};

export const useInventory = (): UseInventoryReturn => {
  const [inventory, setInventory] = useState(defaultInventoryData);
  const [hiddenAssets, setHiddenAssets] = useState<HiddenRecord[]>([]);
  const { setSnack } = useSnackbar();

  const handleFetchInventory = async (forceRefresh = false): Promise<void> => {
    await fetchInventory(forceRefresh);
    setInventory(subject.getValue());
  };

  const fetchInventoryById = async (inventoryId: string): Promise<void> => {
    loadingSubject.next(true);
    const invItem = subject.value.find((inv: InventoryAsset) => inv.id === inventoryId);

    if (invItem) {
      inventoryByIdSubject.next(invItem);
    } else {
      const { data, error } = await fetchAggregateInventoryById(inventoryId, getSelectedAccountIdsForUrl());
      const asset = Array.isArray(data) ? data[0] : (data as InventoryAsset | null);
      if (asset) {
        inventoryByIdSubject.next(normalizeInventoryAsset(asset));
      } else if (error) {
        emitErrorState(error.message);
      } else {
        emitErrorState('Inventory item not found');
      }
    }

    loadingSubject.next(false);
  };

  const deleteInventoryById = async (id: string): Promise<void> => {
    const { error } = await deleteOutsideInventory(id);
    if (error) return setSnack({ message: 'Inventory not deleted', type: 'error', open: true });
    setSnack({ message: 'Inventory deleted successfully', type: 'success', open: true });
  };

  // Refresh the surface the hide/unhide originated from so the record disappears
  // (or reappears) without a manual reload. Benchmark hides are filtered out of
  // the Price Benchmarking table server-side, so a refetch is all that is needed.
  // A Quote hide has no store to refetch: the quotes table holds its own rows and rereads them itself.
  // PriceBenchMark is imported lazily (not at module top level) to break a circular
  // dependency between the Inventory and PriceBenchMark stores: a static import makes
  // PriceBenchMark's module-init account-change watcher observe an undefined accounts
  // subject and skip setup (verified — it breaks PriceBenchMark's watcher spec).
  const refreshSurface = async (source: HiddenAssetSource, accountId: string): Promise<void> => {
    if (source === 'Benchmark') {
      const { fetchPriceBenchmarks } = await import('./PriceBenchMark');
      await fetchPriceBenchmarks(true);
    } else if (source === 'Project') {
      await fetchProjects(true);
    } else if (source === 'Inventory') {
      await handleFetchInventory(true);
    }
    await handleFetchHiddenAssets(accountId, true);
  };

  const handleHideAsset = async (
    assetId: string,
    accId: string,
    source: HiddenAssetSource = 'Inventory'
  ): Promise<boolean> => {
    const accountId = await getAccountIdBySfId(accId);
    const { error } = await createHiddenAsset({ asset_id: assetId, account_id: accountId, source });
    const noun = HIDE_NOUNS[source];
    if (error) {
      setSnack({ message: `Failed to hide ${noun.toLowerCase()}`, type: 'error', open: true });
      return false;
    }

    setSnack({ message: `${noun} hidden successfully`, type: 'success', open: true });
    await refreshSurface(source, accountId);
    return true;
  };

  const handleUnhideAsset = async (
    assetId: string,
    accId: string,
    source: HiddenAssetSource = 'Inventory'
  ): Promise<boolean> => {
    const accountId = await getAccountIdBySfId(accId);
    const { error } = await deleteHiddenAsset(assetId, accountId, source);
    const noun = UNHIDE_NOUNS[source];
    if (error) {
      setSnack({ message: `Failed to unhide ${noun.toLowerCase()}`, type: 'error', open: true });
      return false;
    }

    setSnack({ message: `${noun} unhidden successfully`, type: 'success', open: true });
    await refreshSurface(source, accountId);
    return true;
  };

  const handleFetchHiddenAssets = async (accId: string, forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue() && !forceRefresh) return;
    if (hiddenAssetsSubject.getValue().length > 0 && !forceRefresh) return;
    loadingSubject.next(true);

    const accountId = await getAccountIdBySfId(accId);
    const { data, error } = await fetchHiddenAssets(accountId);

    if (Array.isArray(data)) {
      const assets = data.map((i) => ({ ...normalizeInventoryAsset(i), isHidden: true }));
      hiddenAssetsSubject.next(assets);
      setHiddenAssets(assets);
      emitErrorState();
    } else {
      emitErrorState(error?.message || 'Unexpected inventory response');
      hiddenAssetsSubject.next([]);
    }

    loadingSubject.next(false);
  };

  const setInventoryData = (inventory: InventoryAsset[]) => subject.next(inventory);
  const clear = () => {
    subject.next(defaultInventoryData);
    hiddenAssetsSubject.next([]);
  };

  return {
    setInventoryData,
    clear,
    inventory,
    fetchInventory: handleFetchInventory,
    fetchInventoryById,
    deleteInventoryById,
    deleteHiddenAsset: handleUnhideAsset,
    createHiddenAsset: handleHideAsset,
    fetchHiddenAssets: handleFetchHiddenAssets,
    hiddenAssets
  };
};
